import type { components } from "@/services/customer-schema";
import { faker } from "@faker-js/faker";
import type { AssetsWithAllocations } from "@/services/assets/assetsAdapter.ts";

faker.seed(0);

const ethereumAssetResponse: components["schemas"]["Asset"] = {
  id: "abc1234",
  name: "ETH",
  blockchain: "ETHEREUM",
  balance: {
    pendingDeposit: {
      amount: 1000000172,
      scale: 9,
      amountString: "1.000000172",
      valuation: {
        amount: "2247.65",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 1000000172,
      scale: 9,
      amountString: "1.000000172",
      valuation: {
        amount: "2247.65",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 1000000172,
      scale: 9,
      amountString: "1.000000172",
      valuation: {
        amount: "2247.65",
        currencyCode: "USD",
      },
    },
  },
};

const bitcoinAssetResponse: components["schemas"]["Asset"] = {
  id: "abc12345",
  name: "BTC",
  blockchain: "BITCOIN",
  balance: {
    pendingDeposit: {
      amount: 5000000000,
      scale: 8,
      amountString: "50.00000000",
      valuation: {
        amount: "5072095.75",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 2000000000,
      scale: 8,
      amountString: "20.00000000",
      valuation: {
        amount: "2028838.30",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 3000000000,
      scale: 8,
      amountString: "30.00000000",
      valuation: {
        amount: "3043257.45",
        currencyCode: "USD",
      },
    },
  },
};

const solanaAssetResponse: components["schemas"]["Asset"] = {
  id: "abc123456",
  name: "SOL",
  blockchain: "SOLANA",
  balance: {
    pendingDeposit: {
      amount: 2500000000,
      scale: 9,
      amountString: "2.500000000",
      valuation: {
        amount: "334.50",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 1000000000,
      scale: 9,
      amountString: "1.000000000",
      valuation: {
        amount: "133.80",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 1500000000,
      scale: 9,
      amountString: "1.500000000",
      valuation: {
        amount: "200.70",
        currencyCode: "USD",
      },
    },
  },
};

// Mock zero balance asset
const usdcAssetResponse: components["schemas"]["Asset"] = {
  id: "USDC",
  name: "USDC",
  blockchain: "ETHEREUM",
  balance: {
    pendingDeposit: {
      amount: 0,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 0,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 0,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
  },
};

const xrpAssetResponse: components["schemas"]["Asset"] = {
  id: "XRP",
  name: "XRP",
  blockchain: "XRPL",
  balance: {
    pendingDeposit: {
      amount: 100000000,
      scale: 6,
      amountString: "100.000000",
      valuation: {
        amount: "250.00",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 50000000,
      scale: 6,
      amountString: "50.000000",
      valuation: {
        amount: "125.00",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 350000000,
      scale: 6,
      amountString: "350.000000",
      valuation: {
        amount: "875.00",
        currencyCode: "USD",
      },
    },
  },
};

// Unsupported asset example (no config available)
const unsupportedAssetResponse: components["schemas"]["Asset"] = {
  id: "UNSUP",
  name: "ElseCoin",
  blockchain: "TRON",
  balance: {
    pendingDeposit: {
      amount: 0,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
    pendingWithdrawal: {
      amount: 0,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
    available: {
      amount: 10,
      scale: 9,
      amountString: "0.000000000",
      valuation: {
        amount: "0.00",
        currencyCode: "USD",
      },
    },
  },
};

// TODO pull from accounts.mock.ts accountArrayForEachType
export const accountAssetResponse: components["schemas"]["AssetBalance"] =
  ethereumAssetResponse;

export const accountAssetsResponse: components["schemas"]["AccountAssets"] = {
  assets: [
    { ...ethereumAssetResponse, allocation: 60 },
    { ...bitcoinAssetResponse, allocation: 15 },
    { ...solanaAssetResponse, allocation: 10 },
    { ...xrpAssetResponse, allocation: 15 },
    { ...usdcAssetResponse, allocation: 0 },
    { ...unsupportedAssetResponse, allocation: 0 },
  ],
  pagination: {
    limit: 10,
    firstCursor: "abc123",
    lastCursor: "abc123",
  },
};

export const assetsResponse: components["schemas"]["Assets"] = {
  assets: [
    bitcoinAssetResponse,
    ethereumAssetResponse,
    solanaAssetResponse,
    xrpAssetResponse,
    usdcAssetResponse,
    unsupportedAssetResponse,
  ],

  pagination: {
    limit: 10,
    firstCursor: "abc123",
    lastCursor: "abc123",
  },
};

export const allocationsResponse: AssetsWithAllocations = {
  allocations: [
    {
      ...ethereumAssetResponse,
      allocation: 30.0,
    },
    {
      ...bitcoinAssetResponse,
      allocation: 50.0,
    },
    {
      ...solanaAssetResponse,
      allocation: 15.0,
    },
    {
      ...xrpAssetResponse,
      allocation: 12.0,
    },
    {
      ...usdcAssetResponse,
      allocation: 10.0,
    },
  ],
  remaining: {
    percentage: "5.0",
    valuation: {
      amount: "500.15",
      currencyCode: "USD",
    },
  },
};
